<template>
  <div
    class="paragraph-accordeon mt-5"
    :class="{
      'grid-container': !parentType,
    }"
  >
    <Collapsible
      ref="collapsibleRef"
      :details-class-list="detailsClassList"
      summary-class-list="hover:text-darkGreen"
      :force-open="isEditing"
    >
      <template #summary>
        <h2
          :id="id"
          v-blokkli-editable:field_title
          class="text-xl lg:text-xl lg:font-book"
        >
          {{ title }}
        </h2>
        <SpriteSymbol
          name="chevron-down"
          class="h-6 w-12 text-darkGreen transition-all duration-250 [.open_&]:rotate-180"
          aria-hidden="true"
        />
      </template>

      <template #details>
        <div class="mt-6">
          <BlokkliField :list="paragraphs" name="field_content" />
        </div>
      </template>
    </Collapsible>
  </div>
</template>

<script lang="ts" setup>
import type { ParagraphAccordeonFragment } from '#graphql-operations'
import { slugify } from '#vuepal/helpers/url'
import Collapsible from '../../Collapsible'
import type { CollapsibleInstance } from '../../Collapsible'

const props = defineProps<{
  title: ParagraphAccordeonFragment['title']
  paragraphs: ParagraphAccordeonFragment['paragraphs']

  /**
   * Force the accordion to be always open.
   *
   * We mostly use this in the editor, so that the contents of the accordion
   * are visible.
   */
  alwaysOpen?: boolean
}>()

const { isEditing, parentType } = defineBlokkli({
  bundle: 'accordeon',
  editor: {
    editTitle: (el) => el.querySelector('h2')?.textContent,
    addBehaviour: 'no-form',
    determineVisibleOptions: (ctx) => {
      const options = []
      if (!ctx.parentType) {
        options.push('showInToc')
      }
      return options
    },
  },
  globalOptions: ['showInToc'],
  propsFieldMapping: {
    paragraphs: 'field_content',
  },
})

const id = computed(() => slugify(props.title || ''))
const collapsibleRef = ref<CollapsibleInstance | null>(null)

// Open the accordion when the hash in the URL matches this accordion's ID
onMounted(() => {
  // Check on initial load
  const currentHash = window.location.hash.startsWith('#')
    ? window.location.hash.substring(1)
    : window.location.hash
  if (currentHash === id.value || window.location.hash === `#${id.value}`) {
    openAccordion()
  }

  // Listen for hash changes
  window.addEventListener('hashchange', () => {
    const newHash = window.location.hash.startsWith('#')
      ? window.location.hash.substring(1)
      : window.location.hash
    if (newHash === id.value || window.location.hash === `#${id.value}`) {
      openAccordion()
    }
  })
})

// Method to open the accordion
function openAccordion() {
  if (collapsibleRef.value) {
    // Check if the accordion is already open
    try {
      const isOpenRef = collapsibleRef.value.isOpen as unknown as Ref<boolean>

      if (isOpenRef.value === false) {
        // Use the toggle method to ensure proper animation
        collapsibleRef.value.toggle()
      }
    } catch {
      // Fallback: force the accordion to be open by directly setting the open attribute
      const detailsEl = collapsibleRef.value.detailsEl
      if (detailsEl) {
        // Now we can safely access properties on the HTMLDetailsElement
        const element = detailsEl as unknown as { value: HTMLDetailsElement }
        if (element.value) {
          element.value.open = true
          element.value.classList.add('open')
        }
      } else {
        // Last resort: use the toggle method
        collapsibleRef.value.toggle()
      }
    }
  }
}

const detailsClassList = computed<string[]>(() => {
  const classList = ['py-5', 'border-b-1', 'first:border-t-1']

  if (parentType.value) {
    classList.push('border-darkGreen')
  }

  if (!parentType.value) {
    classList.push(
      'col-span-4',
      'sm:col-span-6',
      'md:col-start-2',
      'md:col-span-6',
      'lg:col-start-3',
      'lg:col-span-8',
      'border-lightGreen',
    )
  }

  return classList
})
</script>

<style lang="postcss">
.paragraph-accordeon {
  + .paragraph-accordeon {
    margin-top: 0;
    details {
      border-top: 0;
    }
  }
}
</style>
